import React, { FC, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { useFormikContext } from 'formik';

import type { ReduxState } from 'app/redux/types';

import { isAlertError } from 'app/api/utils/error-alert-utils';
import { FormControl } from 'app/design-system/components/FormControl';
import { Modal } from 'app/design-system/components/Modal';
import { useTranslation } from 'app/lang';
import { accountFirstName, accountLastName, accountMiddleName } from 'app/lib/account/selectors';
import ModalResponseError from 'app/widgets/ModalResponseError';

import InputField from '../InputField';

import dictionary from './Body.dictionary.json';

import styles from './Body.module.scss';

// ---

const MAX_VALUE_LONG = 50;

type FormikValues = {
  firstName: string;
  lastName: string;
  middleName: string;
};

type StoreProps = {
  firstName?: string;
  lastName?: string;
  middleName?: string;
};

const ModalBody: FC<StoreProps> = ({
  firstName,
  lastName,
  middleName,
}) => {
  const { t } = useTranslation();
  const {
    values,
    setFieldValue,
    errors,
    touched,
  } = useFormikContext<FormikValues>();

  useEffect(() => {
    if (firstName?.length) {
      setFieldValue('firstName', firstName);
    }

    if (lastName?.length) {
      setFieldValue('lastName', lastName);
    }

    if (middleName?.length) {
      setFieldValue('middleName', middleName);
    }
  }, []);

  const changeHandler = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let maxAllowableLengthValue = e.target.value;
    if (e.target.value.length > MAX_VALUE_LONG) {
      maxAllowableLengthValue = e.target.value.slice(0, MAX_VALUE_LONG);
    }
    setFieldValue(e.target.name, maxAllowableLengthValue);
  }, []);

  return (
    <Modal.Body className={styles.body}>
      <p className={styles.reminder}
         data-test="PersonalData_reminder">
        {t(dictionary.reminderText)}
      </p>

      <ul className={styles.personalList}>
        <li className={styles.personalItem}>

          <FormControl label={t(dictionary.lastNameLabel)}
                       error={touched.lastName && errors.lastName}>
            <InputField value={values.lastName}
                        disabled={Boolean(lastName?.length)}
                        data-test="PersonalData_lastName"
                        data-ga-event="profileAddNameInput"
                        data-ga-event-value="LastName"
                        name="lastName"
                        placeholder={t(dictionary.lastNamePlaceholder)}
                        onChange={changeHandler} />
          </FormControl>
        </li>

        <li className={styles.personalItem}>
          <FormControl label={t(dictionary.firstNameLabel)}
                       error={touched.firstName && errors.firstName}>
            <InputField value={values.firstName}
                        disabled={Boolean(firstName?.length)}
                        data-test="PersonalData_firstName"
                        data-ga-event="profileAddNameInput"
                        data-ga-event-value="FirstName"
                        name="firstName"
                        placeholder={t(dictionary.firstNamePlaceholder)}
                        onChange={changeHandler} />
          </FormControl>
        </li>

        <li className={styles.personalItem}>
          <FormControl label={t(dictionary.middleNameLabel)}>
            <InputField value={values.middleName}
                        disabled={Boolean(middleName?.length)}
                        data-test="PersonalData_middleName"
                        data-ga-event="profileAddNameInput"
                        data-ga-event-value="Patronymic"
                        name="middleName"
                        placeholder={t(dictionary.middleNamePlaceholder)}
                        onChange={changeHandler} />
          </FormControl>
        </li>
      </ul>
      <ModalResponseError isError={isAlertError} />
    </Modal.Body>
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => ({
  firstName: accountFirstName(state),
  lastName: accountLastName(state),
  middleName: accountMiddleName(state),
});

export default connect(mapStateToProps)(ModalBody);
